import { QueryKey } from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";

import type { GetReviewCycleDetailsResponseFrontend, ReviewDetailsFrontend } from "@performyard/service-contracts";

import { PYUseQueryOptions } from "../../hooks/StandardQueryArguments";

import {
	getReviewCycleDetails,
	reviewCycleMeta,
	reviewCycleReviewsDetailsStream,
	reviewCycleSubjects,
} from "../../services";

export const reviewCycleMetaQuery = (reviewCycleID?: string) => ({
	queryKey: ["review-cycle", reviewCycleID],
	queryFn: () => reviewCycleMeta(reviewCycleID),
	staleTime: minutesToMilliseconds(2),
	meta: {
		errorMessage: "There was an error getting the review cycle meta data.",
	},
	enabled: !!reviewCycleID,
});

export const reviewCycleSubjectsQuery = (reviewCycleId: string) => ({
	queryKey: ["review-cycle-subjects", reviewCycleId],
	queryFn: () => reviewCycleSubjects(reviewCycleId),
	meta: {
		errorMessage: "There was an error getting the review cycle subjects.",
	},
	enabled: !!reviewCycleId,
});

export const reviewCycleReviewsDetailsStreamQuery = (
	reviewCycleId: string,
	onChunk?: (chunk: { data: ReviewDetailsFrontend[] }) => void
) => {
	const queryKey: QueryKey = ["review-cycle-reviews-details", reviewCycleId, onChunk];
	return {
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey,
		queryFn: () => reviewCycleReviewsDetailsStream(reviewCycleId, queryKey, onChunk),
		meta: {
			errorMessage: "There was an error getting the review cycle reviews details.",
		},
		enabled: !!reviewCycleId,
		staleTime: Number.POSITIVE_INFINITY, // Don't refetch the data (because it's massive and we don't want to refetch it)
		gcTime: 30, // Garbage collect the data 30 seconds after page leave
	};
};

export const reviewCycleDetailsQuery = (
	reviewCycleId: string,
	options?: PYUseQueryOptions<GetReviewCycleDetailsResponseFrontend>
) => ({
	queryKey: ["review-cycle-details", reviewCycleId],
	queryFn: () => getReviewCycleDetails(reviewCycleId),
	staleTime: minutesToMilliseconds(2),
	retry: 3,
	retryDelay: 200,
	meta: {
		errorMessage: "There was an error getting the review cycle details.",
	},
	enabled: !!reviewCycleId,
	...options,
});
